import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {MAX_PLAYBACK_SPEED_MULTIPLIER} from '../configs/playbackSpeed';

const ReplayControlsContext = createContext(null);

export const REPLAY_MIN_SPEED_MULTIPLIER = 0.1;
// Тот же потолок, что и у слайдера демо-показа на главном меню
// (MENU_SHOWCASE_SPEED_MAX в configs/menuShowcase.js) — оба берут его из
// одной общей константы, см. configs/playbackSpeed.js
export const REPLAY_MAX_SPEED_MULTIPLIER = MAX_PLAYBACK_SPEED_MULTIPLIER;
const DEFAULT_SPEED_MULTIPLIER = 1;

// Клики "шаг назад" копятся, а не выполняются немедленно: перемотка назад
// пересчитывает историю с нуля (см. ReplayRunner.fastForwardTo), и это тем
// дороже, чем дальше матч продвинулся — несколько кликов подряд должны
// сложиться в один прыжок на N шагов, а не N отдельных полных пересчетов
export const STEP_BACK_DEBOUNCE_MS = 2000;

// Состояние UI управления просмотром: видимость кнопок боковых панелей
// (PlayersListSideMenu/PolicyMarketSideMenu) используется и в REPLAY, и в
// локальном режиме зрителя — переключается двойным кликом по
// CompletePhaseButton в обоих случаях. Панель воспроизведения (пауза/шаг/
// скорость) — только для REPLAY, локальную партию так не перематываешь.
// Не имеет отношения к игровому состоянию (redux gameState) — это чисто
// зрительский UI-контроль, поэтому вынесен в отдельный React-контекст, а не
// в redux, чтобы не участвовать в записи/детерминизме воспроизведения
export const ReplayControlsProvider = ({children}) => {
  // По умолчанию видимы — двойной клик по CompletePhaseButton в REPLAY их прячет
  const [isPanelsVisible, setIsPanelsVisible] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [speedMultiplier, setSpeedMultiplierState] = useState(DEFAULT_SPEED_MULTIPLIER);
  const [stepRequest, setStepRequest] = useState(null);
  const [pendingStepBackCount, setPendingStepBackCount] = useState(0);
  const [isRewinding, setIsRewinding] = useState(false);

  const stepBackDebounceRef = useRef(null);
  const pendingStepBackCountRef = useRef(0);

  useEffect(() => {
    return () => {
      if (stepBackDebounceRef.current) {
        clearTimeout(stepBackDebounceRef.current);
      }
    };
  }, []);

  const togglePanelsVisible = useCallback(() => {
    setIsPanelsVisible((prev) => !prev);
  }, []);

  // Отмена накопленного шага назад вместе с отложенной дебаунсом перемоткой.
  // Вызывается любой ДРУГОЙ кнопкой панели (пауза/плей, шаг вперед, к началу):
  // команда поверх еще не запущенной перемотки назад конфликтна — например,
  // «шаг вперед» и выстреливший через секунду дебаунс-прыжок на −3 давали бы
  // непредсказуемую итоговую позицию. Новая команда всегда отменяет накопление
  const cancelPendingStepBack = useCallback(() => {
    if (stepBackDebounceRef.current) {
      clearTimeout(stepBackDebounceRef.current);
      stepBackDebounceRef.current = null;
    }
    pendingStepBackCountRef.current = 0;
    setPendingStepBackCount(0);
  }, []);

  const togglePaused = useCallback(() => {
    cancelPendingStepBack();
    setIsPaused((prev) => !prev);
  }, [cancelPendingStepBack]);

  const setSpeedMultiplier = useCallback((value) => {
    const clamped = Math.min(REPLAY_MAX_SPEED_MULTIPLIER, Math.max(REPLAY_MIN_SPEED_MULTIPLIER, value));
    setSpeedMultiplierState(clamped);
  }, []);

  const requestStepForward = useCallback(() => {
    cancelPendingStepBack();
    setIsPaused(true);
    setStepRequest((prev) => ({direction: 'forward', count: 1, nonce: (prev?.nonce ?? 0) + 1}));
  }, [cancelPendingStepBack]);

  // Каждый клик по "шаг назад" копит счетчик и сдвигает 2-секундный дебаунс
  // (см. STEP_BACK_DEBOUNCE_MS) заново; сама перемотка запускается только
  // когда клики закончились — единым прыжком на накопленное число шагов
  const accumulateStepBack = useCallback(() => {
    setIsPaused(true);

    pendingStepBackCountRef.current += 1;
    setPendingStepBackCount(pendingStepBackCountRef.current);

    if (stepBackDebounceRef.current) {
      clearTimeout(stepBackDebounceRef.current);
    }

    stepBackDebounceRef.current = setTimeout(() => {
      stepBackDebounceRef.current = null;
      const count = pendingStepBackCountRef.current;
      pendingStepBackCountRef.current = 0;
      setPendingStepBackCount(0);
      if (count > 0) {
        setStepRequest((prev) => ({direction: 'backward', count, nonce: (prev?.nonce ?? 0) + 1}));
      }
    }, STEP_BACK_DEBOUNCE_MS);
  }, []);

  // Перемотка к самому началу — отдельное направление ('start'), а не большой
  // count в 'backward': ReplayRunner для нее просто сбрасывает до bootstrap
  // без дальнейшей перемотки вперед (см. jumpToStart), это не зависит от
  // текущей позиции. Отменяет любое накопление обычного шага назад — иначе
  // после перемотки к началу мог бы неожиданно сработать устаревший дебаунс
  const requestJumpToStart = useCallback(() => {
    cancelPendingStepBack();
    setIsPaused(true);
    setStepRequest((prev) => ({direction: 'start', nonce: (prev?.nonce ?? 0) + 1}));
  }, [cancelPendingStepBack]);

  const clearStepRequest = useCallback(() => {
    setStepRequest(null);
  }, []);

  return (
    <ReplayControlsContext.Provider
      value={{
        isPanelsVisible,
        togglePanelsVisible,
        isPaused,
        setIsPaused,
        togglePaused,
        speedMultiplier,
        setSpeedMultiplier,
        stepRequest,
        requestStepForward,
        accumulateStepBack,
        requestJumpToStart,
        pendingStepBackCount,
        clearStepRequest,
        isRewinding,
        setIsRewinding,
      }}
    >
      {children}
    </ReplayControlsContext.Provider>
  );
};

export const useReplayControls = () => {
  const context = useContext(ReplayControlsContext);
  if (!context) {
    throw new Error('useReplayControls must be used within a ReplayControlsProvider');
  }
  return context;
};
